/**
 * SN_SAI_KNW_REG_001_POP_001 — AI 지식 등록 (문서에서 추출한 후보) (2026-09-18, 2차 검토 확정)
 *
 * 문서(pdf · docx · xlsx · txt · md · csv)를 끌어다 놓으면 서버가 글을 뽑고 중앙 AI 가 후보를 추려 sai_know_cand 에 쌓는다. 20MB · 20만 자 넘으면 중단.
 * 후보를 여러 개 고르고 팝업의 [적용] 을 누르면 화면이 지식으로 가져온다(사용여부 N). '유사 있음' 이 켜진 후보는 비슷한 지식이 이미 있다.
 * 헤더 쓰레기통 = 무시. 후보는 표에 남아 있으니 다음에 열어 이어서 검토할 수 있다. 추출 중에는 스피너로 팝업을 잠근다(조회 중과 같은 공통 스피너).
 * 파일 전송은 multipart 라 axios 로 직접 (fnUploadLicense 본). 끌어다 놓기는 이 팝업 안의 구역 — 문서 첨부용 공통(SnFileUploadBox)은 첨부 저장소에 붙어 쓰지 않았다.
 *
 * 조회조건 · 컬럼은 팝업 정의(sys_popupctrl · sys_popupgridcolumn, 08_AI_화면설정.sql)에서 온다. 조회는 sn_sai_knw_reg_001_pop_001_01.
 * 여러 줄 선택(rowSelection multiple) 그리드 + 헤더 쓰레기통(onBtnDeleteClick) = 무시. [적용] 은 getOkayData 로 고른 키를 화면에 돌려준다.
 * 본 : 거래처 SN_MAS_BAS_REG_001_POP_001 (getInitPopUpData · getOkayData) · 출하의뢰등록 SN_SAL_ISU_REG_001 상세정보 그리드(헤더 쓰레기통)
 */
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import axios from "axios";
import { global } from "@shared/libs/Global";
import CommonSpinner from "@widgets/CommonSpinner";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import SearchPanel from "@widgets/SearchPanel";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnButton from "@shared/components/SnButton";
import { callApi, fnGetDataSet } from "@shared/api/restApi";
import { showDialog } from "@widgets/CommonDialog";

interface popupProps {
  modalRef?: any;
}

const SN_SAI_KNW_REG_001_POP_001 = forwardRef(({ modalRef }: popupProps, ref: React.Ref<any>) => {
  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({ grid1: [] });
  const [dataSources, setDataSources] = useState<any>({ grid1: [] });
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [dragOver, setDragOver] = useState<boolean>(false);
  /** 올릴 수 있는 크기 — 서버(KnowledgeFileText.MAX_BYTES)와 같은 값. 넘으면 묻지 않고 중단 */
  const MAX_BYTES = 20 * 1024 * 1024;

  useImperativeHandle(ref, () => ({
    /** [적용] — 고른 후보의 키를 화면으로 돌려준다. 없으면 팝업이 안내만 하고 닫히지 않는다 */
    getOkayData: async () => {
      const rows = gridRefs.current["grid1"].current?.getSelectedRows() || [];
      const keys = rows.map((r: any) => r.no_id_know_cand).filter(Boolean);
      if (keys.length === 0) {
        return { closeFlag: false, msgType: "alter", msgText: "가져올 후보를 선택해 주세요. 여러 개를 고를 수 있습니다." };
      }
      return { closeFlag: true, rtnData: keys };
    },
  }));

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitPopUpData("SN_SAI_KNW_REG_001_POP_001", {});
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColums({ grid1: rtn.grid.grid1 || [] });
        await onSearch(rtn.formData);
      } catch (error) {}
    };
    fetchform();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = async (cond?: any) => {
    try {
      const params = {
        id: "sn_sai_knw_reg_001_pop_001_01",
        params: cond ?? searchRef.current?.getFormdata?.() ?? {},
      };
      const data: any = await fnGetDataSet(params);
      setDataSources({ grid1: data?.[0] || [] });
    } catch (error: any) {}
  };

  /** 헤더 쓰레기통 — 고른 후보를 무시한다. 다시 올라오지 않는다 */
  const onIgnore = async () => {
    const rows = gridRefs.current["grid1"].current?.getSelectedRows() || [];
    const keys = rows.map((r: any) => r.no_id_know_cand).filter(Boolean);
    if (keys.length === 0) {
      await showDialog("alter", "무시할 후보를 선택해 주세요.");
      return;
    }
    const confirm = await showDialog("confirm", `${keys.length}건을 무시합니다. 다시 올라오지 않습니다.`);
    if (!confirm) return;
    const result = await callApi("SN_SAI_KNW_REG_001_btnIgnore", { ids: keys });
    if (!result?.status) return;
    await onSearch();
  };

  /** 파일 → 서버(글 뽑기) → 중앙(후보 추리기). 끝나면 다시 읽는다. 도는 동안 스피너로 팝업을 잠근다 */
  const upload = async (file: File | undefined) => {
    if (!file || busy) return;
    if (file.size > MAX_BYTES) {
      await showDialog("alter", "파일이 20MB 를 넘습니다. 문서를 나눠 올려 주세요.");
      return;
    }
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const response = await axios.post("/snotes/SN_SAI_KNW_REG_001_btnExtract", body, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
        },
        withCredentials: true,
      });
      const result = response.data?.result;
      if (result?.if_status !== "S") {
        await showDialog(result?.type === "alter" ? "alter" : "error", result?.message || "추출에 실패했습니다.");
        return;
      }
      await showDialog("success", result.message);
      await onSearch();
    } catch (error: any) {
      await showDialog("error", error?.response?.data?.message || error?.message || "추출 중 오류가 났습니다.");
    } finally {
      setBusy(false);
    }
  };

  const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    await upload(file);
  };

  const onDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    await upload(e.dataTransfer.files?.[0]);
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: { onBtnDeleteClick: onIgnore },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col relative">
      <div className="mb-1">
        <SearchPanel ref={searchRef} data={formData} controls={formControlData} />
      </div>
      <div
        className="mb-1 flex items-center justify-between border-2 border-dashed rounded px-4 py-3 cursor-pointer"
        style={{ borderColor: dragOver ? "#305496" : "#BFBFBF", backgroundColor: dragOver ? "#EEF3FA" : "#FAFAFA" }}
        onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => { if (!busy) fileRef.current?.click(); }}
      >
        <span>{busy ? "AI 가 문서에서 지식 후보를 추리는 중입니다…" : "여기에 문서를 끌어다 놓거나 클릭해서 고르세요"}</span>
        <span className="text-gray-500 text-sm">PDF · Word · Excel · 텍스트, 20MB 까지</span>
        <input ref={fileRef} type="file" accept=".pdf,.docx,.xlsx,.xlsm,.txt,.md,.csv,.json" style={{ display: "none" }} onChange={onFileChosen} />
      </div>
      <CommonSpinner isLoading={busy} />
      <div className="w-full flex h-full min-h-0">
        <SnGrid
          ref={gridRefs.current["grid1"]}
          columnConfig={grids.grid1.columns}
          rowData={grids.grid1.dataSource}
          etcProps={{ rowSelection: "multiple", title: "후보", isEditable: false }}
          events={grids.grid1.events}
          header={
            <div>
              <SnButton text={"조회"} onClick={() => { onSearch(); }} />
            </div>
          }
        />
      </div>
    </div>
  );
});

export default SN_SAI_KNW_REG_001_POP_001;
